import React, {useEffect, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import Loader from "../../components/Loader";
import { getWhatsAppQRUrl, getWhatsAppAuthorizationCode } from "../../api";
import { store } from "react-notifications-component";
import styles from "./whatsapp.module.scss";
import ButtonFilled from "../../components/FormElements/ButtonFilled";

const QrCode = ({ info }) => {
  const { t } = useTranslation();
  const [imgUrl, setImgUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [intervalTime, setIntervalTime] = useState(5);
  const interval = useRef(null);

  // Code authorization states
  const [authCode, setAuthCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeButtonDisabled, setCodeButtonDisabled] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const codeInterval = useRef(null);

  const STORAGE_KEY = `whatsapp_auth_code_disabled_${info?.id}`;
  const DISABLE_DURATION = 60; // 60 seconds = 1 minute

  const showError = (errorTitle, errorMessage) => {
    store.addNotification({
      title: errorTitle,
      message: errorMessage,
      type: "danger",
      insert: "top",
      container: "top-right",
      animationIn: ["animate__animated", "animate__fadeIn"],
      animationOut: ["animate__animated", "animate__fadeOut"],
      dismiss: {
        duration: 5000,
        onScreen: true
      }
    });
  }

  // Check localStorage on mount for persistent disabled state
  useEffect(() => {
    const disabledUntil = localStorage.getItem(STORAGE_KEY);
    if (disabledUntil) {
      const timeRemaining = Math.floor((parseInt(disabledUntil) - Date.now()) / 1000);
      if (timeRemaining > 0) {
        setCodeButtonDisabled(true);
        setRemainingTime(timeRemaining);
        startCountdown(timeRemaining);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    return () => {
      if (codeInterval.current) {
        clearInterval(codeInterval.current);
      }
    };
  }, []);

  const startCountdown = (seconds) => {
    let remaining = seconds;
    setRemainingTime(remaining);

    if (codeInterval.current) {
      clearInterval(codeInterval.current);
    }

    codeInterval.current = setInterval(() => {
      remaining--;
      setRemainingTime(remaining);

      if (remaining <= 0) {
        clearInterval(codeInterval.current);
        setCodeButtonDisabled(false);
        localStorage.removeItem(STORAGE_KEY);
      }
    }, 1000);
  };

  const handleGetAuthCode = async () => {
    if (codeButtonDisabled) return;

    setCodeLoading(true);
    setAuthCode('');

    try {
      const response = await getWhatsAppAuthorizationCode(info.id);

      if (response && response.data && response.data.code) {
        setAuthCode(response.data.code);

        store.addNotification({
          title: "Успешно",
          message: "Код авторизации получен",
          type: "success",
          insert: "top",
          container: "top-right",
          animationIn: ["animate__animated", "animate__fadeIn"],
          animationOut: ["animate__animated", "animate__fadeOut"],
          dismiss: {
            duration: 5000,
            onScreen: true
          }
        });

        // Disable button for 1 minute
        const disabledUntil = Date.now() + (DISABLE_DURATION * 1000);
        localStorage.setItem(STORAGE_KEY, disabledUntil.toString());
        setCodeButtonDisabled(true);
        startCountdown(DISABLE_DURATION);
      }
    } catch (error) {
      let errorMessage = 'Не удалось получить код авторизации';
      if (error && error?.response && error?.response?.data && error?.response?.data?.message) {
        errorMessage = error?.response?.data?.message;
      }
      showError('Ошибка', errorMessage);
    } finally {
      setCodeLoading(false);
    }
  };

  useEffect(() => {
    const id = info.id;

    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }

    let localeIntervalTime = intervalTime;
    let localeLoading = loading;
    interval.current = setInterval(() => {
      if (!localeLoading) {
        if (!localeIntervalTime) {
          localeLoading = true;
          setLoading(true);
          getWhatsAppQRUrl(id)
            .then((res) => {
              if (!res || !res.data) {
                showError(t('NSWhatsapp.errorQR1'), t('NSWhatsapp.errorQR2'))
              }
              // Backend signals "QR not applicable for this channel" with a
              // dedicated error code; stop polling silently — branded channels
              // never go through QR onboarding.
              if (res.data && res.data.code === 10007) {
                localeLoading = false;
                setLoading(false);
                clearInterval(interval.current);
                setSuccess(true);
                return;
              }
              if (res.data && res.data.message) {
                store.addNotification({
                  title: "",
                  message: res.data.message,
                  type: "success",
                  insert: "top",
                  container: "top-right",
                  animationIn: ["animate__animated", "animate__fadeIn"],
                  animationOut: ["animate__animated", "animate__fadeOut"],
                  dismiss: {
                    duration: 5000,
                    onScreen: true
                  }
                });
                localeLoading = false;
                setLoading(false);
                clearInterval(interval.current);
                setSuccess(true);
                return
              }
              setImgUrl(res.data);
              localeLoading = false;
              setLoading(false);
            })
            .catch((error) => {
              let errorMessage = t('NSWhatsapp.errorQR2');
              if (error && error?.response && error?.response?.data && error?.response?.data?.message) {
                errorMessage = error?.response?.data?.message
              }
              showError(t('NSWhatsapp.errorQR1'), errorMessage)
              setImgUrl('');
              localeLoading = false;
              setLoading(false);

            });
          return localeIntervalTime = 20
        } else {
          localeIntervalTime--;
        }
        setIntervalTime(localeIntervalTime);
      }
    }, 1000);

    return () => {
      clearInterval(interval.current);
    }
  }, []);

  return (
    <>
      {
        loading ? (<span>{t('NSWhatsapp.loadQR')}</span>)
        : !success ? (<span>{t('NSWhatsapp.update', {seconds: intervalTime >= 10 ? `00:${intervalTime}` : `00:0${intervalTime}`})}</span>) : null
      }
      {!success && (
        <div className={styles.img}>
          {(loading || !imgUrl) && <Loader color="#73D389" style={{ marginTop: '100px' }} />}
          {/*{(imgUrl && !loading) && <img src={imgUrl} alt="QR code"/>}*/}
          {(imgUrl && !loading) && <iframe src={imgUrl} title="QR code"/>}
        </div>
      )}

      {!success && (
        <div style={{ marginTop: '30px', textAlign: 'center' }}>
          <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'center' }}>
            <ButtonFilled
              onClick={handleGetAuthCode}
              loading={codeLoading}
              disabled={codeButtonDisabled}
              style={{ minWidth: '320px', whiteSpace: 'nowrap' }}
            >
              {codeButtonDisabled && remainingTime > 0
                ? `Авторизоваться через код (${remainingTime}с)`
                : 'Авторизоваться через код'}
            </ButtonFilled>
          </div>

          {authCode && (
            <div style={{
              backgroundColor: '#f0f0f0',
              padding: '20px',
              borderRadius: '8px',
              marginTop: '15px',
              maxWidth: '400px',
              margin: '15px auto 0'
            }}>
              <p style={{ marginBottom: '10px', fontWeight: '500' }}>Код авторизации:</p>
              <p style={{
                fontSize: '32px',
                fontWeight: 'bold',
                letterSpacing: '8px',
                fontFamily: 'monospace',
                color: '#2b8a3e'
              }}>
                {authCode}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default QrCode;